import React, { useState } from "react";
import {
  EllipsisVertical,
  LucideBell,
  LucidePill,
  LucideTarget,
  Settings,
} from "lucide-react";
import Popover from "src/components/common/Popover";
import SetGoalsModal from "src/components/app/modals/SetGoalsModal";
import SetSleepReminderModal from "src/components/app/modals/sleep/SetSleepReminderModal";
import SetWaterReminderModal from "src/components/app/modals/water/SetWaterReminderModal";
import SetStepsReminderModal from "src/components/app/modals/steps/SetStepsReminderModal";
import SetFatigueReminderModal from "src/components/app/modals/fatigue/SetFatigueReminderModal";
import SetDietReminderModal from "src/components/app/modals/diet/SetDietReminderModal";
import SetMedicationReminderModal from "src/components/app/modals/medication/SetMedicationReminderModal";
import SetExerciseReminderModal from "src/components/app/modals/exercise/SetExerciseReminderModal";
import UpdateReminderConfirmationModal from "src/components/app/modals/UpdateReminderConfirmationModal";
import { useAppSelector } from "src/redux/hooks";
import {
  getExistingReminderByLogKey,
  ReminderData,
} from "src/helpers/reminderHelpers";

interface InsightsPopoverProps {
  type: string;
}

const InsightsPopover: React.FC<InsightsPopoverProps> = ({ type }) => {
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showSetGoalsModal, setShowSetGoalsModal] = useState(false);
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [existingReminder, setExistingReminder] = useState<ReminderData | null>(
    null
  );

  const { reminder } = useAppSelector((state) => state.reminder);

  // Check if there are existing reminders for this log type
  const existingReminderForType = getExistingReminderByLogKey(
    reminder?.data?.reminders,
    type
  );

  const handleReminderClick = () => {
    if (existingReminderForType) {
      setExistingReminder(existingReminderForType);
      setShowUpdateConfirmation(true);
    } else {
      setShowReminderModal(true);
    }
  };

  const handleUpdateConfirm = () => {
    setShowUpdateConfirmation(false);
    setShowReminderModal(true);
  };

  const handleUpdateCancel = () => {
    setShowUpdateConfirmation(false);
    setExistingReminder(null);
  };

  const goalTypes = {
    "water-logs": "water",
    "sleep-logs": "sleep",
    "steps-logs": "steps",
    "fatigue-logs": "fatigue",
    "diet-logs": "diet",
    "medication-logs": "medication",
    "exercise-logs": "exercise",
  };

  const getReminderModal = (type: string) => {
    switch (type) {
      case "water-logs":
        return (
          <SetWaterReminderModal
            show={showReminderModal}
            handleClose={() => setShowReminderModal(false)}
            existingReminder={existingReminder}
          />
        );
      case "sleep-logs":
        return (
          <SetSleepReminderModal
            show={showReminderModal}
            handleClose={() => setShowReminderModal(false)}
            existingReminder={existingReminder}
          />
        );
      case "steps-logs":
        return (
          <SetStepsReminderModal
            show={showReminderModal}
            handleClose={() => setShowReminderModal(false)}
            existingReminder={existingReminder}
          />
        );
      case "fatigue-logs":
        return (
          <SetFatigueReminderModal
            show={showReminderModal}
            handleClose={() => setShowReminderModal(false)}
            existingReminder={existingReminder}
          />
        );
      case "diet-logs":
        return (
          <SetDietReminderModal
            show={showReminderModal}
            handleClose={() => setShowReminderModal(false)}
            existingReminder={existingReminder}
          />
        );
      case "medication-logs":
        return (
          <SetMedicationReminderModal
            show={showReminderModal}
            handleClose={() => setShowReminderModal(false)}
          />
        );
      case "exercise-logs":
        return (
          <SetExerciseReminderModal
            show={showReminderModal}
            handleClose={() => setShowReminderModal(false)}
            existingReminder={existingReminder}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Popover label={<EllipsisVertical className="w-5 h-5" />}>
        <ul className="w-full">
          {type === "medication-logs" ? (
            <>
              <li className="w-full block">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                  onClick={() => setShowReminderModal(true)}
                >
                  <LucidePill className="w-4 h-4" /> Add Medication
                </button>
              </li>
            </>
          ) : type === "diet-logs" ? (
            <li className="w-full block">
              <button
                type="button"
                className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                onClick={handleReminderClick}
              >
                <LucideBell className="w-4 h-4" />
                {existingReminderForType ? "Update Reminder" : "Set Reminder"}
              </button>
            </li>
          ) : (
            <>
              <li className="w-full block">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                  onClick={() => setShowSetGoalsModal(true)}
                >
                  <LucideTarget className="w-4 h-4" /> Set Goals
                </button>
              </li>
              <li className="w-full block">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                  onClick={handleReminderClick}
                >
                  {existingReminderForType ? (
                    <>
                      <Settings className="w-4 h-4" /> Update Reminder
                    </>
                  ) : (
                    <>
                      <LucideBell className="w-4 h-4" /> Set Reminder
                    </>
                  )}
                </button>
              </li>
            </>
          )}
        </ul>
      </Popover>
      {showSetGoalsModal && (
        <SetGoalsModal
          show={showSetGoalsModal}
          handleClose={() => setShowSetGoalsModal(false)}
          type={goalTypes[type as keyof typeof goalTypes]}
        />
      )}
      {showReminderModal && getReminderModal(type)}
      {showUpdateConfirmation && (
        <UpdateReminderConfirmationModal
          show={showUpdateConfirmation}
          handleClose={handleUpdateCancel}
          onConfirm={handleUpdateConfirm}
          onCancel={handleUpdateCancel}
          reminder={existingReminder}
        />
      )}
    </>
  );
};

export default InsightsPopover;
